import React, { useEffect, useState, useCallback } from "react";
import {
    fetchTransactionAccountMaps,
    fetchTransactionTypes,
    fetchUtilAccounts,
    fetchCatalogs, // New import for fetching catalogs
    fetchCatentriesForCatalog, // New import for fetching catalog entries
} from "../apiHelper";
import TransactionMappingManager from "./transactionmappingmanager/TransactionMappingManager";
import ServiceCatalogMappingManager from "./servicecatalogmappingmanager/ServiceCatalogMappingManager"; // New import for the service catalog manager
import Button from '../components/ui/Button';
import { useToast } from "../hooks/useToast";
import { BarChart2, GitCommit, Settings, BookOpen, Loader2, XCircle, Info } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const MappingManager = () => {
    const { showToast } = useToast();
    const navigate = useNavigate()

    const [user, setUser] = useState(null);
    const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'transactionMappings', 'catentryMappings'
    const [glAccounts, setGlAccounts] = useState([]);
    const [TransactionTypeEnum, setTransactionTypeEnum] = useState({});
    const [transactionMappings, setTransactionMappings] = useState([]);
    const [serviceCatalogMappings, setServiceCatalogMappings] = useState([]); // New state for service catalog mappings
    const [serviceCatalogEntries, setServiceCatalogEntries] = useState([]); // New state for service catalog entries
    const [serviceCatalogId, setServiceCatalogId] = useState(null); // New state to hold the service catalog ID

    // Loading and error states for various data sources
    const [loadingGlAccounts, setLoadingGlAccounts] = useState(true);
    const [errorGlAccounts, setErrorGlAccounts] = useState(null);
    const [loadingTransactionTypes, setLoadingTransactionTypes] = useState(true);
    const [errorTransactionTypes, setErrorTransactionTypes] = useState(null);
    const [loadingTransactionMaps, setLoadingTransactionMaps] = useState(true);
    const [errorTransactionMaps, setErrorTransactionMaps] = useState(null);
    const [loadingServiceCatalogs, setLoadingServiceCatalogs] = useState(true); // New loading state
    const [errorServiceCatalogs, setErrorServiceCatalogs] = useState(null); // New error state

    const loadTransactionTypes = useCallback(async () => {
        setLoadingTransactionTypes(true)
        setErrorTransactionTypes(null)
        try {
            const fetchedTransactionTypes = await fetchTransactionTypes()
            const TransactionTypeEnum = fetchedTransactionTypes.reduce((acc, current) => {
                const type = current.transaction_type;
                acc[type] = type;
                return acc;
            }, {})
            setTransactionTypeEnum(TransactionTypeEnum)
        } catch (err) {
            const errMsg = err?.response?.data?.detail || "Error loading transaction types.";
            showToast(errMsg, 'error');
            setErrorGlAccounts(errMsg);
            console.error('Error loading transaction types:', err)
        } finally {
            setLoadingTransactionTypes(false);
        }
    }, [showToast])

    // Load GL Accounts
    const loadUtilAccounts = useCallback(async (loggedInUser) => {
        if (!loggedInUser) return;
        setLoadingGlAccounts(true);
        setErrorGlAccounts(null);
        try {
            const fetchedUtilAccounts = await fetchUtilAccounts(loggedInUser);
            const glAcccs = fetchedUtilAccounts.map(account => ({
                gl_account_id: account.account_id,
                account_number: account.account_code,
                account_name: account.name,
                account_type: account.account_type.label,
                is_active: account.is_active,
                parent_account_id: account.parent_id,
            }))
            setGlAccounts(glAcccs);
        } catch (err) {
            const errMsg = err?.response?.data?.detail || "Error loading chart of accounts.";
            showToast(errMsg, 'error');
            setErrorGlAccounts(errMsg);
            console.error('Error loading chart of accounts:', err);
        } finally {
            setLoadingGlAccounts(false);
        }
    }, [showToast]);

    // Load Transaction Account Maps
    const loadTransactionAccountMaps = useCallback(async (loggedInUser) => {
        if (!loggedInUser) return;
        setLoadingTransactionMaps(true);
        setErrorTransactionMaps(null);
        try {
            const fetchedTMaps = await fetchTransactionAccountMaps({...loggedInUser, offset:0, limit:100000});
            // console.log(loggedInUser)
            setTransactionMappings(fetchedTMaps);
        } catch (err) {
            const errMsg = err?.response?.data?.detail || "Error loading transaction account maps.";
            showToast(errMsg, 'error');
            setErrorTransactionMaps(errMsg);
            console.error(`Error loading transaction account maps:`, err);
        } finally {
            setLoadingTransactionMaps(false);
        }
    }, [showToast]);

    // New function to load service catalog entries
    const loadServiceCatalogEntries = useCallback(async (loggedInUser) => {
        if (!loggedInUser) return;
        setLoadingServiceCatalogs(true);
        setErrorServiceCatalogs(null);
        try {
            const catalogs = await fetchCatalogs(loggedInUser);
            const serviceCatalog = catalogs.find(cat => cat.identifier === 'Service Catalog');
            if (!serviceCatalog) {
                throw new Error("Service Catalog not found.");
            }
            setServiceCatalogId(serviceCatalog.catalog_id);

            const fetchedEntries = await fetchCatentriesForCatalog({
                ...loggedInUser,
                catalog_id: serviceCatalog.catalog_id,
                offset: 0,
                limit: 100 // Fetch first 100 entries, pagination can be added later
            });
            setServiceCatalogEntries(fetchedEntries);
        } catch (err) {
            const errMsg = err?.response?.data?.detail || `Error loading service catalog entries: ${err.message}`;
            showToast(errMsg, 'error');
            setErrorServiceCatalogs(errMsg);
            console.error('Error loading service catalog entries:', err);
        } finally {
            setLoadingServiceCatalogs(false);
        }
    }, [showToast]);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to manage mappings.", "info");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    // Effect to load data when user is available
    useEffect(() => {
        if (user) {
            loadUtilAccounts(user);
            loadTransactionAccountMaps(user);
            loadTransactionTypes();
            loadServiceCatalogEntries(user); // New call to load service catalog data
        }
    }, [user, loadUtilAccounts, loadTransactionAccountMaps, loadTransactionTypes, loadServiceCatalogEntries]);

    // Render logic based on activeView
    const renderActiveView = () => {
        if (loadingGlAccounts || loadingTransactionMaps || loadingServiceCatalogs) {
            return (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={48} className="animate-spin text-gmail-blue" />
                    <p className="text-xl text-text-secondary ml-4">Loading mapping data...</p>
                </div>
            );
        }

        if (errorGlAccounts || errorTransactionMaps || errorServiceCatalogs) {
            return (
                <div className="flex flex-col items-center justify-center py-20 text-gmail-red">
                    <XCircle size={48} className="mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">Error Loading Mappings</h3>
                    <p className="text-md text-center">{errorGlAccounts || errorTransactionMaps || errorServiceCatalogs}</p>
                    <Button onClick={() => { if (user) { loadUtilAccounts(user); loadTransactionAccountMaps(user); loadTransactionTypes(); loadServiceCatalogEntries(user); } }} variant="primary" className="mt-6">
                        Retry Loading
                    </Button>
                </div>
            );
        }

        if (!user) {
            return (
                <div className="flex flex-col items-center justify-center py-20 text-orange-600">
                    <Info size={48} className="mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">Authentication Required</h3>
                    <p className="text-md text-center">Please log in to view and manage accounting mappings.</p>
                </div>
            );
        }

        switch (activeView) {
            case 'transactionMappings':
                return (
                    <TransactionMappingManager
                        transactionMappings={transactionMappings}
                        setTransactionMappings={setTransactionMappings}
                        glAccounts={glAccounts}
                        TransactionTypeEnum={TransactionTypeEnum}
                        onSaveSuccess={() => loadTransactionAccountMaps(user)}
                        onTransactionTypeAdded={() => loadTransactionTypes()}
                        user={user}
                    />
                );
            case 'catentryMappings':
                return (
                    <ServiceCatalogMappingManager
                        transactionTypes={Object.values(TransactionTypeEnum)}
                        serviceCatalogEntries={serviceCatalogEntries}
                        onTransactionTypeAdded={() => loadTransactionTypes()}
                        onSaveSuccess={() => { /* Reload service catalog mappings */ }}
                        user={user}
                    />
                );
            default: // Dashboard View
                return (
                    <div className="p-8 bg-white rounded-lg shadow-md flex flex-col items-center justify-center text-center">
                        <h2 className="text-3xl font-bold mb-4 text-text-primary">Accounting Mappings Dashboard</h2>
                        <p className="text-text-secondary mb-8 max-w-xl">
                            Configure how various business transactions and catalog entries are automatically posted to your General Ledger (GL) accounts.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
                            <Button
                                variant="secondary"
                                onClick={() => setActiveView('transactionMappings')}
                                className="flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            >
                                <GitCommit size={48} className="mb-3 text-gmail-blue" />
                                Manage General Transaction Mappings
                                <span className="text-sm font-normal text-text-secondary mt-2">
                                    Define default debit/credit accounts for transaction types (e.g., sales, purchases, payments).
                                </span>
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setActiveView('catentryMappings')}
                                className="flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            >
                                <BookOpen size={48} className="mb-3 text-gmail-green" />
                                Manage Catalog Entry Mappings
                                <span className="text-sm font-normal text-text-secondary mt-2">
                                    Map specific products or services to unique GL accounts.
                                </span>
                            </Button>
                        </div>
                        <div className="mt-10 text-sm text-gray-600 max-w-2xl text-center">
                            <p className="text-xs text-muted-foreground mt-4">
                                <em>
                                    <strong>Important Note:</strong> These settings directly impact your financial records. Only experienced Accounting Professionals should make changes here.
                                </em>
                            </p>
                            <p className="mt-2">
                                GL Account numbers are unique. Transaction Types and Catalog Entry Mapping Types are also unique per Catalog Entry.
                            </p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col flex-1 bg-gray-100 min-h-screen">
            <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8 w-full">
                <h1 className="text-5xl font-extrabold text-center text-gmail-blue mb-10 leading-tight">
                    <BarChart2 size={56} className="inline-block mr-4 align-middle" />
                    Accounting Setup & Mappings
                </h1>

                {activeView !== 'dashboard' && (
                    <div className="mb-6">
                        <Button
                            variant="secondary"
                            onClick={() => setActiveView('dashboard')}
                            className="flex items-center gap-2 px-4 py-2 text-md"
                        >
                            &larr; Back to Dashboard
                        </Button>
                    </div>
                )}
                {renderActiveView()}
            </div>
        </div>
    );
};

export default MappingManager;
